/// <reference types="./project.d.mts" />
import * as $timestamp_ from "../../common_common/gleam/time/timestamp/extra.mjs";
import * as $json from "../../gleam_json/gleam/json.mjs";
import * as $dict from "../../gleam_stdlib/gleam/dict.mjs";
import * as $decode from "../../gleam_stdlib/gleam/dynamic/decode.mjs";
import * as $function from "../../gleam_stdlib/gleam/function.mjs";
import * as $list from "../../gleam_stdlib/gleam/list.mjs";
import * as $option from "../../gleam_stdlib/gleam/option.mjs";
import * as $result from "../../gleam_stdlib/gleam/result.mjs";
import * as $timestamp from "../../gleam_time/gleam/time/timestamp.mjs";
import * as $mime_types from "../../mime_types/mime_types.mjs";
import * as $status from "../data/status.mjs";
import * as $decrypt from "../decrypt.mjs";
import { Ok, toList, prepend as listPrepend, CustomType as $CustomType, isEqual } from "../gleam.mjs";

export class Metadata extends $CustomType {
  constructor(attachments, integrations) {
    super();
    this.attachments = attachments;
    this.integrations = integrations;
  }
}

export class Attachment extends $CustomType {
  constructor(name, content_type) {
    super();
    this.name = name;
    this.content_type = content_type;
  }
}

export class Salesforce extends $CustomType {
  constructor(case_id) {
    super();
    this.case_id = case_id;
  }
}

export class Project extends $CustomType {
  constructor(id, name, owners, org_id, deadline, client, collaborators, metadata, created_at, status) {
    super();
    this.id = id;
    this.name = name;
    this.owners = owners;
    this.org_id = org_id;
    this.deadline = deadline;
    this.client = client;
    this.collaborators = collaborators;
    this.metadata = metadata;
    this.created_at = created_at;
    this.status = status;
  }
}

export function toggle_collaborator(project, user_id) {
  let all_collaborators$1 = $list.append(project.owners, project.collaborators);
  let is_collaborator = $list.contains(all_collaborators$1, user_id);
  let _record = project;
  return new Project(
    _record.id,
    _record.name,
    _record.owners,
    _record.org_id,
    _record.deadline,
    _record.client,
    (() => {
      if (is_collaborator) {
        return $list.filter(
          project.collaborators,
          (u) => { return u !== user_id; },
        );
      } else {
        return listPrepend(user_id, project.collaborators);
      }
    })(),
    _record.metadata,
    _record.created_at,
    _record.status,
  );
}

function decode_integration() {
  return $decode.field(
    "case_id",
    $decode.string,
    (case_id) => { return $decode.success(new Salesforce(case_id)); },
  );
}

function metadata_decoder() {
  return $decrypt.optional_field(
    "attachments",
    $decode.list(
      $decode.field(
        "name",
        $decode.string,
        (name) => {
          return $decode.field(
            "content_type",
            $decode.string,
            (content_type) => {
              let $ = $mime_types.from_string(content_type);
              if ($ instanceof Ok) {
                let content_type$1 = $[0];
                return $decode.success(new Attachment(name, content_type$1));
              } else {
                return $decode.failure(
                  new Attachment("", new $mime_types.Csv()),
                  "Attachment",
                );
              }
            },
          );
        },
      ),
    ),
    (attachments) => {
      let attachments$1 = $option.unwrap(attachments, toList([]));
      return $decrypt.optional_field(
        "integrations",
        $decode.dict($decode.string, decode_integration()),
        (integrations) => {
          let integrations$1 = $option.unwrap(integrations, $dict.new$());
          return $decode.success(new Metadata(attachments$1, integrations$1));
        },
      );
    },
  );
}

function encode_integration(integration) {
  return $json.object(toList([["case_id", $json.string(integration.case_id)]]));
}

export function encode_metadata(metadata) {
  return $json.object(
    toList([
      ["languages", $json.preprocessed_array(toList([]))],
      ["default_language", $json.null$()],
      [
        "attachments",
        $json.array(
          metadata.attachments,
          (attachment) => {
            let content_type = $mime_types.to_string(attachment.content_type);
            return $json.object(
              toList([
                ["name", $json.string(attachment.name)],
                ["content_type", $json.string(content_type)],
              ]),
            );
          },
        ),
      ],
      [
        "integrations",
        $json.dict(
          metadata.integrations,
          $function.identity,
          encode_integration,
        ),
      ],
    ]),
  );
}

export function decoder() {
  return $decode.field(
    "id",
    $decode.string,
    (id) => {
      return $decode.field(
        "name",
        $decode.string,
        (name) => {
          return $decode.field(
            "owners",
            (() => {
              let owner = $decode.map($decode.string, $list.wrap);
              let owners = $decode.list($decode.string);
              return $decode.one_of(owners, toList([owner]));
            })(),
            (owners) => {
              return $decode.field(
                "org_id",
                $decode.string,
                (org_id) => {
                  return $decode.field(
                    "deadline",
                    $decrypt.timestamp(),
                    (deadline) => {
                      return $decode.field(
                        "client",
                        $decode.string,
                        (client) => {
                          return $decode.field(
                            "collaborators",
                            $decode.list($decode.string),
                            (collaborators) => {
                              return $decode.field(
                                "metadata",
                                $decrypt.json(metadata_decoder()),
                                (metadata) => {
                                  return $decode.field(
                                    "created_at",
                                    $decrypt.timestamp(),
                                    (created_at) => {
                                      return $decode.field(
                                        "status",
                                        $status.decoder(),
                                        (status) => {
                                          return $decode.success(
                                            new Project(
                                              id,
                                              name,
                                              owners,
                                              org_id,
                                              deadline,
                                              client,
                                              collaborators,
                                              metadata,
                                              created_at,
                                              status,
                                            ),
                                          );
                                        },
                                      );
                                    },
                                  );
                                },
                              );
                            },
                          );
                        },
                      );
                    },
                  );
                },
              );
            },
          );
        },
      );
    },
  );
}

export function encode(project) {
  return $json.object(
    toList([
      ["id", $json.string(project.id)],
      ["name", $json.string(project.name)],
      ["owner", $json.string("")],
      ["owners", $json.array(project.owners, $json.string)],
      ["org_id", $json.string(project.org_id)],
      ["deadline", $timestamp_.encode(project.deadline)],
      ["client", $json.string(project.client)],
      ["collaborators", $json.array(project.collaborators, $json.string)],
      ["metadata", encode_metadata(project.metadata)],
      ["created_at", $timestamp_.encode(project.created_at)],
      ["status", $status.encode(project.status)],
    ]),
  );
}

export function all_collaborators(project) {
  let _pipe = $list.append(project.owners, project.collaborators);
  return $list.unique(_pipe);
}

export function completed(project) {
  return !isEqual(project.status, new $status.InProgress());
}

export function name(project) {
  let $ = project.name;
  if ($ === "") {
    return "Untitled";
  } else {
    let name$1 = $;
    return name$1;
  }
}

export const salesforce = "salesforce";

export function salesforce_id(project) {
  let _pipe = project.metadata.integrations;
  let _pipe$1 = $dict.get(_pipe, salesforce);
  return $result.try$(
    _pipe$1,
    (i) => {
      let case_id = i.case_id;
      return new Ok(case_id);
    },
  );
}
